import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../services/api';

const isDev = process.env.NODE_ENV === 'development';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();

  // Dev-only form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleMicrosoftLogin = async () => {
    // Generate PKCE code_verifier (random 32-byte base64url string)
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    const verifier = btoa(String.fromCharCode(...Array.from(array)))
      .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');

    // Derive code_challenge = BASE64URL(SHA-256(verifier))
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
    const challenge = btoa(String.fromCharCode(...Array.from(new Uint8Array(digest))))
      .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');

    localStorage.setItem('pkce_verifier', verifier);

    const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    window.location.href = `${apiBase}/auth/microsoft/login?code_challenge=${challenge}&code_challenge_method=S256`;
  };

  const handleDevSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await authApi.login(email, password);
      login(data.access_token, data.role, data.full_name);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Login failed. Check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen dark:bg-gsw-navy bg-gray-100 flex items-center justify-center p-4 transition-colors duration-200">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="h-[3px]" style={{ background: 'linear-gradient(90deg, #C9A84C, #E8C97A, #C9A84C)' }} />

        <div className="p-10">
          <div className="mb-8 text-center">
            <img
              src="/gsw_logo.png"
              alt="GoldStandard Wealth"
              className="h-14 mx-auto mb-4 object-contain"
            />
            <h1 className="text-2xl font-semibold text-gsw-navy font-playfair">
              GSW Quant Index Platform
            </h1>
            <p className="text-gray-500 mt-1 text-sm">Sign in with your GoldStandard Wealth account</p>
          </div>

          {/* Microsoft SSO button */}
          <button
            onClick={handleMicrosoftLogin}
            className="w-full flex items-center justify-center gap-3 border border-gray-300 rounded-lg py-2.5 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
          >
            {/* Microsoft logo SVG */}
            <svg width="20" height="20" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
              <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
              <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
              <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
            </svg>
            Sign in with Microsoft
          </button>

          {/* Dev-only password form */}
          {isDev && (
            <div className="mt-8">
              <div className="relative flex items-center mb-5">
                <div className="flex-grow border-t border-gray-200" />
                <span className="mx-3 text-xs text-gray-400 shrink-0">Dev login</span>
                <div className="flex-grow border-t border-gray-200" />
              </div>

              <form onSubmit={handleDevSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gsw-gold focus:border-transparent transition"
                    placeholder="you@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gsw-gold focus:border-transparent transition"
                    placeholder="••••••••"
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gsw-gold text-gsw-navy rounded-lg py-2.5 text-sm font-semibold hover:bg-gsw-gold-light disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  {loading ? 'Signing in...' : 'Sign in'}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
